import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import AuthControls from '../components/AuthControls';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';
import usePageTitle from '../hooks/usePageTitle';
import { isDevConsoleAllowed } from '../dev/devConsoleAccess';

function DevPage() {
  usePageTitle('Developer Tools');

  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useApp();

  // Decided in render, not in an effect: an effect runs after the first
  // paint, which showed the console to every other signed-in user for a frame.
  const allowed = isDevConsoleAllowed(user);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (!allowed) {
      navigate('/home', { replace: true });
    }
  }, [loading, isAuthenticated, allowed, navigate]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated || !allowed) {
    return null;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <AuthControls />
      </div>
      <div className="flex gap-6">
        <div className="w-72">
          <h2 className="mb-2 text-lg font-semibold">Create Organization</h2>
          <div className="rounded border p-4">
            <CreateOrganizationSection />
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateOrganizationSection() {
  const functions = getFunctions();
  const createOrg = httpsCallable(functions, 'createOrganization');

  const [orgName, setOrgName] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [studyLocationTimeout, setStudyLocationTimeout] = useState('20');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    setSubmitting(true);
    try {
      const res = await createOrg({ orgName, orgCode, licenseId, studyLocationTimeout: Number(studyLocationTimeout) });
      if (res?.data?.success) {
        setResult({ ok: true, message: 'Organization Created!' });
        setOrgName('');
        setOrgCode('');
        setLicenseId('');
        setStudyLocationTimeout('20');
      } else {
        setResult({ ok: false, message: 'Unknown error' });
      }
    } catch (err) {
      setResult({ ok: false, message: err.code || 'internal-error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div>
        <label htmlFor="orgName" className="block text-sm font-medium text-slate-700">Organization Name</label>
        <input id="orgName" value={orgName} onChange={(e) => setOrgName(e.target.value)} required className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
      </div>
      <div>
        <label htmlFor="orgCode" className="block text-sm font-medium text-slate-700">Organization Code</label>
        <input id="orgCode" value={orgCode} onChange={(e) => setOrgCode(e.target.value)} required className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
      </div>
      <div>
        <label htmlFor="licenseId" className="block text-sm font-medium text-slate-700">License ID</label>
        <input id="licenseId" value={licenseId} onChange={(e) => setLicenseId(e.target.value)} required className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
      </div>
      <div>
        <label htmlFor="studyLocationTimeout" className="block text-sm font-medium text-slate-700">Study Location Timeout (seconds)</label>
        <input id="studyLocationTimeout" type="number" min="0" value={studyLocationTimeout} onChange={(e) => setStudyLocationTimeout(e.target.value)} required className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
      </div>
      <button type="submit" disabled={submitting} className="w-full rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create'}
      </button>
      {result && (
        <div className={`text-xs ${result.ok ? 'text-green-600' : 'text-red-600'}`}>
          {result.message}
        </div>
      )}
    </form>
  );
}

export default DevPage;

